'use client';

import {
  Box,
  Flex,
  Heading,
  Image,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { animate, scroll } from 'motion';
import { motion, useScroll, useTransform } from 'framer-motion';
import { render } from 'datocms-structured-text-to-html-string';
import StructuredTextParser from '../../app/landing_pages/karma/Components/StructuredTextParser';

const MotionFlex = motion(Flex);

export default function LightUpSection({ sectionContent }) {
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const groupRef = useRef(null);
  const viewportRef = useRef(null);
  const scrollAnimationRef = useRef(null);
  const [triggerRecalc, setTriggerRecalc] = useState(0);
  const cards = sectionContent?.[2]?.cards;

  // Track horizontal scroll progress for text motion
  const { scrollYProgress: horizontalProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  let titleTranslateYThreshold = useBreakpointValue({
    base: '0',
    md: '-230px',
    lg: '-200px',
    '2xl': '-220px',
  });

  // Move title upward as cards scroll
  const translateY = useTransform(
    horizontalProgress,
    [0, 0.25],
    ['0px', titleTranslateYThreshold]
  );

  function getPercentage(value, percent) {
    return (value * percent) / 100;
  }

  const calculateAndAnimateScroll = () => {
    if (!groupRef.current || !containerRef.current || !viewportRef.current)
      return;

    // Clean up previous animation if exists
    if (scrollAnimationRef.current) {
      scrollAnimationRef.current();
      scrollAnimationRef.current = null;
    }

    const leftStickyWidth =
      document.querySelector('.start-sticky-text')?.offsetWidth || 0;
    const lightUpCard =
      document.querySelector('.light-up-card')?.offsetWidth || 0;
    const marginSpace =
      parseFloat(
        getComputedStyle(document.querySelector('.card-group'))?.gap
      ) || 0;

    // Get width from ref instead of window
    const currentWindowWidth = viewportRef.current.offsetWidth;
    const spaceTotalWidth = (cards.length - 1) * marginSpace;

    let scrollWidth = 0;
    if (currentWindowWidth < 768) {
      scrollWidth =
        cards.length * lightUpCard + spaceTotalWidth + 32 - currentWindowWidth; // padding
    } else if (currentWindowWidth < 960) {
      scrollWidth = scrollWidth =
        leftStickyWidth +
        cards.length * lightUpCard +
        spaceTotalWidth -
        Math.min(currentWindowWidth, 1202) +
        64; // padding
    } else {
      scrollWidth =
        leftStickyWidth +
        cards.length * lightUpCard +
        spaceTotalWidth -
        Math.min(currentWindowWidth, 1202) +
        32; // padding
    }

    // Reset transform before applying new animation
    if (groupRef.current) {
      groupRef.current.style.transform = 'none';
    }

    // Create new scroll animation and store cleanup function
    scrollAnimationRef.current = scroll(
      animate(groupRef.current, {
        transform: ['none', `translateX(-${scrollWidth}px)`],
      }),
      { target: containerRef.current }
    );
  };

  useEffect(() => {
    // Calculate scroll on mount and when triggerRecalc changes
    calculateAndAnimateScroll();
  }, [triggerRecalc, sectionContent]);

  useEffect(() => {
    if (!sectionRef.current) return;

    let timeoutId;

    // Create ResizeObserver to watch section resize
    const resizeObserver = new ResizeObserver(entries => {
      // Debounce the callback
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setTriggerRecalc(prev => prev + 1);
      }, 150);
    });

    // Start observing the section
    resizeObserver.observe(sectionRef.current);

    return () => {
      // Clean up observer and timeout
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
      // Clean up animation on unmount
      if (scrollAnimationRef.current) {
        scrollAnimationRef.current();
      }
    };
  }, []);

  // Track scroll progress for fading out the heading text
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);

  return (
    <Box as="section" w="100%" ref={sectionRef}>
      <Box
        ref={containerRef}
        className="card-group-container"
        h={`${cards.length * 100}vh`} // dynamic like reference: 1 card = 100vh
        position="relative"
      >
        {/* Sticky viewport section */}
        <Box
          ref={viewportRef}
          position="sticky"
          top="0"
          h="100vh"
          w="100%"
          overflow="hidden"
          display="flex"
          alignItems="end"
          justifyContent="flex-start"
          bgImage={`url(${sectionContent?.[1]?.image?.url})`}
          bgSize="cover"
          bgPos="center"
          color="white"
        >
          {/* Overlay text */}
          <Flex
            h="max"
            flexDirection={{ base: 'column', md: 'row' }}
            pl={{ base: '16px', md: '16px' }}
            pr={{ base: '16px', md: '16px' }}
            maxW={'1202px'}
            mx={'auto'}
          >
            {/* Horizontal card group */}
            <MotionFlex
              direction="column"
              align="flex-start"
              style={{ y: translateY }}
              position="sticky"
              mb={{ base: '24px', md: '102px', '2xl': '160px' }}
              w="max-content"
              zIndex="1"
              className={'start-sticky-text'}
              minW={{ base: 'max-content', md: '388px' }}
              justifyContent={'center'}
              mr={{ base: '0px', md: '32px', lg: '0px' }}
            >
              <Text
                fontSize={{ base: '24px', md: '32px' }}
                letterSpacing={{ base: '3.2px', md: '7.12px' }}
                lineHeight={{ base: '24px', md: '32px' }}
                fontFamily="FedraSansStd-book"
                className="allSmallCase"
                pb={'8px'}
              >
                {sectionContent?.[0]?.titleText}
              </Text>
              <Heading
                fontSize={{ base: '56px', md: '72px' }}
                letterSpacing={{ base: '-1.12px', md: '-1.44px' }}
                lineHeight={{ base: '56px', md: '72px' }}
                fontFamily="FedraSerifDisplayRegular"
                textTransform={'lowercase'}
                fontWeight={'normal'}
              >
                {sectionContent?.[0]?.titleSubtext}
              </Heading>
            </MotionFlex>
            <Flex
              ref={groupRef}
              className="card-group"
              zIndex="1"
              gap={{ base: '16px', md: '32px' }}
              justifyContent={'flex-end'}
              mb={{ base: '78px', md: '102px', xl: '60px', '2xl': '120px' }}
            >
              {sectionContent?.[2]?.cards?.map((card, i) => (
                <Flex
                  key={i}
                  className="light-up-card"
                  w={{ base: '296px', md: '478px', xl: '569px' }}
                  align="center"
                  justify="center"
                  flex={{ base: '0 0 296px', md: '0 0 478px', xl: '0 0 569px' }}
                  px={{ base: '16px', md: '20px' }}
                  py={{ base: '24px', md: '24px' }}
                  flexDirection={'column'}
                  alignItems={'start'}
                  background={'#fff'}
                  borderRadius={'4px'}
                  justifyContent={'flex-start'}
                >
                  <Image
                    src={card?.thumbnail?.url}
                    alt={card?.thumbnail?.alt || ''}
                    maxW="100%"
                    maxH={{ base: '40px', md: '56px' }}
                    objectFit="cover"
                  />
                  <Heading
                    as="h3"
                    fontSize={{ base: '24px', md: '28px' }}
                    letterSpacing={{ base: '2.88px', md: '3.84px' }}
                    lineHeight={{ base: '21.6px', md: '28.8px' }}
                    textTransform="uppercase"
                    mt={{ base: '12px', md: '14px' }}
                    mb={{ base: '16px', md: '24px' }}
                    color={'#CF4520'}
                    fontWeight={'500'}
                    fontFamily="FedraSansStd-medium"
                    className="allSmallCase"
                  >
                    {card.title}
                  </Heading>
                  <StructuredTextParser
                    str={render(card?.description)}
                    sectionContent={card}
                    fontSize={{ base: '14px', md: '18px' }}
                    letterSpacing={{ base: '0.28px', md: '0.36px' }}
                    lineHeight={{ base: '19.6px', md: '25.2px' }}
                    fontFamily="FedraSansStd-book"
                    color={'#1D1A16'}
                    mt="0px"
                  />
                </Flex>
              ))}
            </Flex>
          </Flex>
        </Box>
      </Box>
      <style jsx global>{`
        .allSmallCase {
          font-variant: all-small-caps;
        }
        @media (max-width: 767px) {
          .start-sticky-text {
            opacity: 1 !important;
          }
        }
      `}</style>
    </Box>
  );
}
